import { useEffect, useState } from "react";
import { CalendarCheck, Loader2, Phone, Store } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const JOURS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const DEFAUT = { 0: ["09:00", "17:00"], 1: ["09:00", "17:00"], 2: ["09:00", "17:00"], 3: ["09:00", "17:00"], 4: ["09:00", "17:00"], 5: null, 6: null };

const timeCls = "bg-ink border border-ivory/15 rounded-lg px-2 py-1.5 text-sm text-ivory [color-scheme:dark] focus:border-gold focus:outline-none transition-colors";

export default function RendezVousAdminCard({ shopSettings, setShopSettings }) {
  const [horaire, setHoraire] = useState(() => ({ ...DEFAUT, ...(shopSettings?.rdv_horaire || {}) }));
  const [durees, setDurees] = useState(() => ({ boutique: 30, telephone: 15, libre: 30, ...(shopSettings?.rdv_durees || {}) }));
  const [actif, setActif] = useState(() => shopSettings?.rdv_actif !== false);
  const [fermetures, setFermetures] = useState(() => shopSettings?.rdv_fermetures || []);
  const [ferme, setFerme] = useState({ du: "", au: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [rdvs, setRdvs] = useState(null);

  useEffect(() => {
    if (shopSettings?.rdv_horaire) setHoraire((h) => ({ ...DEFAUT, ...shopSettings.rdv_horaire }));
  }, [shopSettings?.rdv_horaire]);
  useEffect(() => {
    if (shopSettings?.rdv_durees) setDurees((d) => ({ ...d, ...shopSettings.rdv_durees }));
  }, [shopSettings?.rdv_durees]);
  useEffect(() => {
    if (shopSettings?.rdv_actif !== undefined) setActif(shopSettings.rdv_actif !== false);
    if (shopSettings?.rdv_fermetures) setFermetures(shopSettings.rdv_fermetures);
  }, [shopSettings?.rdv_actif, shopSettings?.rdv_fermetures]);

  const chargeRdvs = () => api.get("/admin/rendezvous")
    .then((r) => setRdvs(r.data)).catch(() => setRdvs({ a_venir: [], passes: [] }));
  useEffect(() => { chargeRdvs(); }, []);

  const setPlage = (jour, idx, val) =>
    setHoraire((h) => {
      const plage = [...(h[jour] || ["09:00", "17:00"])];
      plage[idx] = val;
      return { ...h, [jour]: plage };
    });

  const sauvegarder = async () => {
    setSaving(true);
    try {
      const propre = Object.fromEntries(Object.entries(horaire).map(([j, p]) => [j, p && p[0] && p[1] ? p : null]));
      const ferm = fermetures.filter((f) => f.du);
      await api.patch("/admin/settings", { rdv_horaire: propre, rdv_durees: durees, rdv_actif: actif, rdv_fermetures: ferm });
      setShopSettings?.((s) => ({ ...s, rdv_horaire: propre, rdv_durees: durees, rdv_actif: actif, rdv_fermetures: ferm }));
      toast.success("Réglages des rendez-vous enregistrés — appliqués aussitôt sur la page de réservation");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const fmtQuand = (iso) => new Date(iso).toLocaleString("fr-CA",
    { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

  // Marqueur « venu / absent » sur les rendez-vous passés (demande du propriétaire)
  const marquerPresence = async (id, presence) => {
    const actuelle = rdvs?.passes.find((r) => r.id === id)?.presence;
    const nouvelle = actuelle === presence ? null : presence; // reclic = efface
    try {
      await api.post(`/admin/rendezvous/${id}/presence`, { presence: nouvelle });
      setRdvs((d) => ({ ...d, passes: d.passes.map((r) => r.id === id ? { ...r, presence: nouvelle } : r) }));
      toast.success(nouvelle ? `Rendez-vous marqué « ${nouvelle} »` : "Marqueur retiré");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="bg-surface border border-ivory/10 rounded-xl p-7 mb-6" data-testid="rdv-admin-card">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="font-semibold">Rendez-vous clients — horaire et réservations</p>
        {/* Accès direct à l'agenda Google du propriétaire (demande 2026-10-20) */}
        <a href="https://calendar.google.com" target="_blank" rel="noreferrer"
          data-testid="rdv-ouvrir-agenda"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold border border-gold/50 text-gold hover:bg-gold hover:text-ink transition-colors">
          <CalendarCheck className="w-3.5 h-3.5" /> Ouvrir mon Google Agenda
        </a>
      </div>
      <p className="text-xs text-ivory/70 mt-1">
        Les clients réservent sur la page « Prendre rendez-vous » : ils écrivent leur type de
        rendez-vous (case libre), choisissent date et heure. Chaque réservation s'inscrit automatiquement
        dans votre Google Agenda connecté ci-dessus, avec courriel de confirmation au client, rappel la
        veille au client et rappel à l'atelier 1 h avant.
      </p>

      {/* Horaire d'ouverture modifiable */}
      <div className="mt-5">
        <p className="text-sm font-semibold text-champagne mb-3">Heures d'ouverture pour les rendez-vous</p>
        {/* 2026-10-28 (demande du propriétaire : « corriger le décalage ») :
            2 colonnes SEULEMENT sur grand écran — sur mobile/tablette, une
            rangée propre par jour ; sinon la colonne de droite chevauchait les
            menus d'heures de celle de gauche */}
        <div className="grid gap-2 lg:grid-cols-2">
          {JOURS.map((jour, i) => (
            <div key={i} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 min-w-0" data-testid={`rdv-horaire-${i}`}>
              <label className="flex items-center gap-2 w-24 shrink-0 cursor-pointer">
                <input type="checkbox" checked={!!horaire[i]} data-testid={`rdv-jour-${i}-ouvert`}
                  onChange={(e) => setHoraire((h) => ({ ...h, [i]: e.target.checked ? (h[i] || ["09:00", "17:00"]) : null }))}
                  className="accent-[#d4af37]" />
                <span className="text-sm">{jour}</span>
              </label>
              {horaire[i] ? (
                <div className="flex items-center gap-1.5 min-w-0">
                  <input type="time" value={horaire[i][0]} onChange={(e) => setPlage(i, 0, e.target.value)}
                    className={`${timeCls} w-28 min-w-0`} data-testid={`rdv-jour-${i}-debut`} />
                  <span className="text-xs text-ivory/50">à</span>
                  <input type="time" value={horaire[i][1]} onChange={(e) => setPlage(i, 1, e.target.value)}
                    className={`${timeCls} w-28 min-w-0`} data-testid={`rdv-jour-${i}-fin`} />
                </div>
              ) : <span className="text-xs text-ivory/40 italic">Fermé</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Durée par type (demande du propriétaire) : ex. 30 min en boutique,
          15 min au téléphone — multiple de 15 min */}
      <div className="mt-5">
        <p className="text-sm font-semibold text-champagne mb-3">Durée d'un rendez-vous</p>
        <div className="flex flex-wrap items-center gap-4">
          {[["libre", "Type libre (défaut)"], ["boutique", "Visite en boutique"], ["telephone", "Appel téléphonique"]].map(([t, label]) => (
            <label key={t} className="flex items-center gap-2 text-sm" data-testid={`rdv-duree-${t}`}>
              {label}
              <select value={durees[t]} onChange={(e) => setDurees((d) => ({ ...d, [t]: Number(e.target.value) }))}
                className="bg-ink border border-ivory/15 rounded-lg px-2 py-1.5 text-sm text-ivory focus:border-gold focus:outline-none transition-colors">
                {[15, 30, 45, 60, 90, 120].map((m) => <option key={m} value={m}>{m} min</option>)}
              </select>
            </label>
          ))}
        </div>
      </div>

      {/* Interrupteur + fermetures ponctuelles (demande du propriétaire,
          2026-10-20) : activer/désactiver la prise de rendez-vous, fériés,
          vacances… La note est visible par les clients. */}
      <div className="mt-5 space-y-4">
        <label className="flex items-center gap-3 cursor-pointer" data-testid="rdv-actif-toggle">
          <input type="checkbox" checked={actif} onChange={(e) => setActif(e.target.checked)}
            className="accent-[#d4af37] w-4 h-4" />
          <span className="text-sm">
            <strong>Prise de rendez-vous en ligne {actif ? "ACTIVE" : "FERMÉE"}</strong>
            <span className="block text-xs text-ivory/50">
              {actif ? "Les clients peuvent réserver sur le site."
                     : "La page affiche « réservations fermées » — aucun nouveau rendez-vous possible."}
            </span>
          </span>
        </label>
        <div>
          <p className="text-sm font-semibold text-champagne mb-2">Fermetures (fériés, vacances…)</p>
          {fermetures.length > 0 && (
            <ul className="space-y-1.5 mb-3" data-testid="rdv-fermetures-liste">
              {fermetures.map((f, i) => (
                <li key={i} className="flex items-center gap-3 text-sm bg-ink/40 border border-ivory/10 rounded-lg px-3.5 py-2">
                  <span className="text-ivory/80">{f.du}{f.au && f.au !== f.du ? ` → ${f.au}` : ""}</span>
                  <span className="text-xs text-ivory/50 flex-1">{f.note}</span>
                  <button type="button" onClick={() => setFermetures((fs) => fs.filter((_, j) => j !== i))}
                    data-testid={`rdv-fermeture-del-${i}`}
                    className="text-red-400/70 hover:text-red-300 text-xs transition-colors">Retirer</button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap items-center gap-2" data-testid="rdv-fermeture-form">
            <input type="date" value={ferme.du} onChange={(e) => setFerme((f) => ({ ...f, du: e.target.value }))}
              className={`${timeCls} min-w-0`} data-testid="rdv-fermeture-du" title="Du" />
            <span className="text-xs text-ivory/50">au</span>
            <input type="date" value={ferme.au} onChange={(e) => setFerme((f) => ({ ...f, au: e.target.value }))}
              className={`${timeCls} min-w-0`} data-testid="rdv-fermeture-au" title="Au (vide = un seul jour)" />
            <input type="text" value={ferme.note} onChange={(e) => setFerme((f) => ({ ...f, note: e.target.value }))}
              placeholder="Note visible des clients (ex. : Vacances de la construction)"
              className="bg-ink border border-ivory/15 rounded-lg px-3 py-1.5 text-sm text-ivory w-full sm:flex-1 sm:min-w-48 focus:border-gold focus:outline-none transition-colors"
              data-testid="rdv-fermeture-note" />
            <button type="button" data-testid="rdv-fermeture-add"
              onClick={() => { if (ferme.du) { setFermetures((fs) => [...fs, { ...ferme }]); setFerme({ du: "", au: "", note: "" }); } }}
              className="px-3.5 py-1.5 rounded-full text-xs font-semibold border border-gold/50 text-gold hover:bg-gold hover:text-ink transition-colors">
              + Ajouter
            </button>
          </div>
          <p className="text-[10px] text-ivory/45 mt-1.5">Ces dates affichent « fermé » (avec votre note) et ne peuvent pas être réservées.</p>
        </div>
      </div>

      <button type="button" onClick={sauvegarder} disabled={saving} data-testid="rdv-horaire-save"
        className="mt-4 px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors disabled:opacity-50 inline-flex items-center gap-2">
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Enregistrer l'horaire
      </button>

      {/* Rendez-vous à venir */}
      <div className="mt-6 border-t border-ivory/10 pt-5">
        <p className="text-sm font-semibold text-champagne mb-3">Rendez-vous à venir</p>
        {!rdvs && <Loader2 className="w-4 h-4 animate-spin text-gold" />}
        {rdvs && rdvs.a_venir.length === 0 && (
          <p className="text-xs text-ivory/50" data-testid="rdv-admin-vide">Aucun rendez-vous à venir pour l'instant.</p>
        )}
        {rdvs && rdvs.a_venir.length > 0 && (
          <ul className="space-y-2" data-testid="rdv-admin-liste">
            {rdvs.a_venir.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm bg-ink/40 border border-ivory/10 rounded-lg px-4 py-2.5" data-testid={`rdv-admin-item-${r.id}`}>
                <span className="font-semibold text-gold">{fmtQuand(r.debut)}</span>
                <span className="inline-flex items-center gap-1.5 text-ivory/80">
                  {r.type === "boutique" ? <Store className="w-3.5 h-3.5" /> : <Phone className="w-3.5 h-3.5" />}
                  {r.type === "libre" ? (r.type_label || "Rendez-vous") : r.type === "boutique" ? "Boutique" : "Téléphone"}{r.duree ? ` · ${r.duree} min` : ""}
                </span>
                <span className="text-ivory">{r.name}</span>
                <span className="text-xs text-ivory/50">{r.phone} — {r.email}</span>
                {r.sujet && <span className="w-full text-xs text-champagne/80 italic">« {r.sujet} »</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Rendez-vous passés : marqueurs « venu / absent » (demande du propriétaire) */}
      {rdvs && rdvs.passes.filter((r) => r.statut === "confirme").length > 0 && (
        <div className="mt-6 border-t border-ivory/10 pt-5">
          <p className="text-sm font-semibold text-champagne mb-3">Rendez-vous passés — suivi des absences</p>
          <ul className="space-y-2" data-testid="rdv-admin-passes">
            {rdvs.passes.filter((r) => r.statut === "confirme").slice(0, 20).map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm bg-ink/40 border border-ivory/10 rounded-lg px-4 py-2.5" data-testid={`rdv-admin-passe-${r.id}`}>
                <span className="text-ivory/60">{fmtQuand(r.debut)}</span>
                <span className="inline-flex items-center gap-1.5 text-ivory/80">
                  {r.type === "boutique" ? <Store className="w-3.5 h-3.5" /> : <Phone className="w-3.5 h-3.5" />}
                  {r.type === "libre" ? (r.type_label || "Rendez-vous") : r.type === "boutique" ? "Boutique" : "Téléphone"}
                </span>
                <span className="text-ivory">{r.name}</span>
                {r.sujet && <span className="text-xs text-champagne/80 italic">« {r.sujet} »</span>}
                <span className="ml-auto flex items-center gap-2">
                  <button type="button" data-testid={`rdv-venu-${r.id}`}
                    onClick={() => marquerPresence(r.id, "venu")}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${r.presence === "venu"
                      ? "bg-emerald-500 text-ink" : "border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10"}`}>
                    Venu
                  </button>
                  <button type="button" data-testid={`rdv-absent-${r.id}`}
                    onClick={() => marquerPresence(r.id, "absent")}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${r.presence === "absent"
                      ? "bg-red-500 text-ink" : "border border-red-400/40 text-red-300 hover:bg-red-400/10"}`}>
                    Absent
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
